/**
 * @file src/permissions/ownerRoles.js
 * @description Who works FOR Moksy. One list, imported, never retyped.
 *
 * ── Why this file exists ─────────────────────────────────────────────
 *
 * This list was maintained by hand in EIGHT places — `models/User.js`,
 * `RankPolicy.js`, and six separate copies inside the dashboard — each with a
 * comment asking the next editor to remember the others. They happened to
 * agree; that was luck, not design.
 *
 * The failure mode is silent, which is what makes it worth fixing before it
 * bites rather than after. Adding a staff role and missing one copy does not
 * break a build or fail a test: it produces a staff member who cannot see
 * their own console, or who is quietly treated as a customer by one screen
 * and an operator by the next. That is the same shape as the admin role chart
 * that rendered nine hardcoded roles out of forty-five and hid 84 of 95
 * accounts — nothing errored, the number was just wrong.
 *
 * It lives in packages/common rather than in users-service because the
 * DASHBOARD holds most of the copies, and a browser cannot import from a
 * service. The list had to move somewhere both sides already depend on.
 *
 * ── What this is NOT ─────────────────────────────────────────────────
 *
 * Not `OPERATOR_ROLES` in reviewerSeparation.js. That is a deliberately
 * narrower set — seven roles, including the synthetic `system` actor —
 * answering "who may bypass the reviewer/customer separation". Merging the
 * two would silently widen a rule about platform integrity to every staff
 * role including `content_moderator`. Two questions, two lists, on purpose.
 *
 * Not an authorization check either. Being owner-group decides which SURFACES
 * exist, not what may be done on them; the per-route scope checks remain the
 * authority. See audit findings #3 and #43 for why that distinction is
 * currently muddier than it should be.
 */

/**
 * Ordered by seniority, most senior first.
 *
 * The order is load-bearing for anything that picks a "primary" staff role
 * from an account holding several — `roleForPersona` in personas.js resolves
 * the staff hat by taking the first match — so a new role must be inserted at
 * its rank, not appended.
 */
export const OWNER_ROLES = Object.freeze([
  'super_admin',
  'chief_operating_officer',
  'chief_compliance_officer',
  'chief_financial_officer',
  'chief_security_officer',
  'admin',
  'head_of_platform',
  'head_of_support',
  'auditor',
  'manager',
  'data_protection_officer',
  'finance',
  'finance_controller',
  'operations_manager',
  'incident_responder',
  'release_manager',
  'reviewer_coordinator',
  'support_lead',
  'support',
  'content_moderator'
]);

/** Set form, for the callers that were building their own. */
export const OWNER_ROLE_SET = Object.freeze(new Set(OWNER_ROLES));

/**
 * Rank within the owner group — who may look through whose eyes.
 *
 * ── Why this lives beside the list rather than in auth ───────────────
 *
 * It was in three places: `foundation/auth/src/policies/RankPolicy.js` (the
 * real one, 20 roles), and 5-row copies in the gateway and the dashboard,
 * each asserting in comment that it agreed with the other two. It did not.
 * Fifteen of the twenty roles ranked 0 in both copies, which is not a
 * cosmetic drift: `canImpersonate` compares ranks, so a head of platform
 * (75) could not support-as anybody in owner-group, and a front-line support
 * (30, but 40 in the gateway's table) passed a check against a chief security
 * officer (90, but 0 there) — caught only by auth re-running the real policy.
 *
 * A rank is a property of a role. Keeping the two apart is what let them
 * disagree, so they are now one table. auth keeps its own copy on purpose —
 * see models/User.js on why a core auth constant must not depend on a package
 * resolving — and `ownerRolesDrift.test.js` is what keeps that copy honest.
 *
 * Ordered high to low, matching OWNER_ROLES above.
 */
export const OWNER_RANK = Object.freeze({
  // Executive
  super_admin:               100,
  chief_operating_officer:    95,
  chief_compliance_officer:   90,
  chief_financial_officer:    90,
  chief_security_officer:     90,
  // Senior leadership
  admin:                      80,
  head_of_platform:           75,
  head_of_support:            70,
  // Department operators
  auditor:                    65,  // read-only, ranked high so juniors cannot reach it
  manager:                    60,
  data_protection_officer:    55,
  finance:                    55,
  finance_controller:         50,
  operations_manager:         50,
  // Specialists
  incident_responder:         45,
  release_manager:            40,
  reviewer_coordinator:       40,
  support_lead:               35,
  // Front-line
  support:                    30,
  content_moderator:          25,
  // Everyone outside the owner group.
  user: 0
});

/** Numeric rank; anything not in the table is user-group baseline. */
export const rankForRole = (role) => OWNER_RANK[String(role || '').trim().toLowerCase()] || 0;

/**
 * May `actor` impersonate `target`?
 *
 * Owner-group only, and strictly downward within it — no peer or self
 * impersonation. `{ role, group? }` shapes; group is derived when absent so
 * fixtures stay terse.
 *
 * This decides authority, NOT permission: the two-person approval
 * (requireImpersonationApproval) is a separate gate and both must pass.
 */
export const canImpersonate = (actor = {}, target = {}) => {
  const norm = (v) => String(v || '').trim().toLowerCase();
  const aGroup = norm(actor.group) || groupForRole(actor.role);
  const tGroup = norm(target.group) || groupForRole(target.role);
  if (aGroup !== USER_GROUPS.OWNER) return { allow: false, reason: 'ACTOR_NOT_OWNER_GROUP' };
  if (tGroup === USER_GROUPS.USER) return { allow: true, reason: 'OWNER_OVER_USER' };
  return rankForRole(actor.role) > rankForRole(target.role)
    ? { allow: true, reason: 'RANK_DOWNWARD' }
    : { allow: false, reason: 'RANK_NOT_DOWNWARD' };
};

// A role with no rank is a role nobody can place in the hierarchy, which is
// how the 5-row copies failed silently. Fail at import instead.
{
  const missing = OWNER_ROLES.filter((r) => !(r in OWNER_RANK));
  if (missing.length) throw new Error(`owner roles with no rank: ${missing.join(', ')}`);
}

export const USER_GROUPS = Object.freeze({ OWNER: 'owner', USER: 'user' });

/**
 * Is this an owner-group role?
 *
 * Normalises, because the same role string arrives from a JWT claim, an
 * `x-user` header and a form field, and one of them will have different
 * casing or a stray space eventually.
 */
export const isOwnerRole = (role) => OWNER_ROLE_SET.has(String(role || '').trim().toLowerCase());

/** The `group` a role derives. */
export const groupForRole = (role) => (isOwnerRole(role) ? USER_GROUPS.OWNER : USER_GROUPS.USER);

export default OWNER_ROLES;
